import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Menu, X, ChevronDown, CheckCircle } from 'lucide-react';
import { COMPANY_INFO, NAVIGATION_MENU } from '../data';

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
    setActiveDropdown(null);
  }, [location]);

  const isActive = (path) => {
    if (path === '#' || !path) return false;
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white shadow-md py-3 border-b border-brand-light'
          : 'bg-white/95 backdrop-blur-md py-4 border-b border-brand-light'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <img
              src="https://assets.auxodev.dk/cdn-cgi/image/width=154,quality=90,format=auto/assets/8d0f5bf2-9099-44a5-baa5-a4be578e55f8/energirude-eksperten-logo.png"
              alt="Energirude Eksperten - Glarmester i Egedal, Nordsjælland og store dele af Sjælland"
              className="h-12 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation (>= 1200px) */}
          <nav className="hidden xl:flex items-center gap-8">
            {NAVIGATION_MENU.map((item, idx) => {
              if (item.children) {
                const isChildActive = item.children.some((child) => isActive(child.href));
                return (
                  <div
                    key={idx}
                    className="relative group"
                    onMouseEnter={() => setActiveDropdown(idx)}
                    onMouseLeave={() => setActiveDropdown(null)}
                  >
                    <button
                      className={`flex items-center gap-1 text-[15px] font-medium transition-colors hover:text-brand-blue py-2 ${
                        isChildActive ? 'text-brand-blue font-semibold' : 'text-brand-dark'
                      }`}
                    >
                      {item.label}
                      <ChevronDown className="w-4 h-4 transition-transform group-hover:rotate-180" />
                    </button>
                    <div className="absolute top-full left-0 w-56 bg-white rounded-lg shadow-lg border border-brand-light py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                      {item.children.map((child, cIdx) => (
                        <Link
                          key={cIdx}
                          to={child.href}
                          className={`block px-4 py-2 text-sm transition-colors hover:bg-brand-light hover:text-brand-blue ${
                            isActive(child.href) ? 'bg-brand-light text-brand-blue font-semibold' : 'text-brand-dark'
                          }`}
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                );
              }

              return (
                <Link
                  key={idx}
                  to={item.href}
                  className={`text-[15px] font-medium transition-colors hover:text-brand-blue py-2 relative ${
                    isActive(item.href) ? 'text-brand-blue font-semibold' : 'text-brand-dark'
                  }`}
                >
                  {item.label}
                  {isActive(item.href) && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-blue rounded-full" />
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Contact & CTA */}
          <div className="hidden xl:flex items-center gap-6">
            <a
              href={`tel:${COMPANY_INFO.phone.replace(/\s+/g, '')}`}
              className="flex items-center gap-2 text-brand-deep hover:text-brand-blue transition-colors font-semibold whitespace-nowrap"
            >
              <span className="bg-brand-light p-2 rounded-full">
                <Phone className="w-4 h-4 text-brand-blue" />
              </span>
              <span className="text-lg">{COMPANY_INFO.phone}</span>
            </a>
            <Link
              to="/gratis-besoeg"
              className="bg-brand-blue hover:bg-brand-deep text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200 shadow-md hover:shadow-lg"
            >
              Book gratis besøg
            </Link>
          </div>

          {/* Mobile Menu Button (< 1200px) */}
          <div className="flex xl:hidden items-center gap-4">
            <a
              href={`tel:${COMPANY_INFO.phone.replace(/\s+/g, '')}`}
              className="flex items-center gap-1.5 text-brand-deep font-bold text-sm bg-brand-light px-3 py-2 rounded-lg"
            >
              <Phone className="w-4 h-4 text-brand-blue" />
              <span className="whitespace-nowrap">{COMPANY_INFO.phone}</span>
            </a>
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 rounded-lg text-brand-dark hover:bg-brand-light transition-colors"
              aria-label="Åbn menu"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation Panel */}
      {isOpen && (
        <div className="xl:hidden bg-white border-t border-brand-light shadow-inner max-h-[calc(100vh-80px)] overflow-y-auto">
          <div className="px-4 pt-4 pb-6 space-y-3">
            {NAVIGATION_MENU.map((item, idx) => {
              if (item.children) {
                return (
                  <div key={idx} className="space-y-1">
                    <button
                      onClick={() => setActiveDropdown(activeDropdown === idx ? null : idx)}
                      className="flex items-center justify-between w-full px-3 py-2.5 text-base font-medium text-brand-dark rounded-lg hover:bg-brand-light transition-colors"
                    >
                      <span>{item.label}</span>
                      <ChevronDown
                        className={`w-5 h-5 transition-transform duration-200 ${
                          activeDropdown === idx ? 'rotate-180 text-brand-blue' : ''
                        }`}
                      />
                    </button>
                    {activeDropdown === idx && (
                      <div className="pl-4 space-y-1 bg-brand-light/40 rounded-lg py-1">
                        {item.children.map((child, cIdx) => (
                          <Link
                            key={cIdx}
                            to={child.href}
                            className={`block px-3 py-2 text-sm rounded-md transition-colors ${
                              isActive(child.href)
                                ? 'bg-brand-light text-brand-blue font-semibold'
                                : 'text-brand-dark hover:bg-brand-light'
                            }`}
                          >
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <Link
                  key={idx}
                  to={item.href}
                  className={`block px-3 py-2.5 text-base font-medium rounded-lg transition-colors ${
                    isActive(item.href)
                      ? 'bg-brand-light text-brand-blue font-semibold'
                      : 'text-brand-dark hover:bg-brand-light'
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}

            <div className="pt-4 border-t border-brand-light space-y-3">
              <Link
                to="/gratis-besoeg"
                className="block w-full text-center bg-brand-blue hover:bg-brand-deep text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200"
              >
                Book gratis besøg
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
